'use client';

import { FormGroup, FormInputErrors } from '~/components/primitives';
import type { I18n } from '@funkey/locales';
import React from 'react';
import { PARTNER_TYPE_ACTIVITY, PARTNER_TYPE_REFERRAL } from '@funkey/shared/constants';
import { useI18n } from '~/features/i18n';
import { usePartnerRegistrationCtx } from '../context';
import { getInputComponent } from '~/features/form';
import { std } from '@funkey/shared/utils';

export default function PartnerRegistrationPage() {
    const i18n = useI18n();
    const ctx = usePartnerRegistrationCtx();

    const {
        formState: { handleChange, getErrorMessages, values },
    } = ctx;

    return (
        <div className="grid-cols-2 gap-6 lg:grid">
            {std.Object.entries(fields(i18n)).map(([field, config]) => (
                <FormGroup key={field} label={config.label} className="col-span-1" required>
                    {getInputComponent({
                        type: config.type,
                        field,
                        value: values[field],
                        handleChange,
                        options: config.type === 'select' ? config.options : undefined,
                        placeholder: config.type === 'select' ? config.placeholder : undefined,
                        id: config.type === 'select' ? config.id : undefined,
                    })}
                    <FormInputErrors errors={getErrorMessages(field)} />
                </FormGroup>
            ))}
        </div>
    );
}

const fields = (i18n: I18n) =>
    ({
        type: {
            required: true,
            type: 'select',
            label: i18n._('Type'),
            placeholder: i18n._('Please select a partner type'),
            id: 'partner-type-select',
            options: [
                { value: PARTNER_TYPE_ACTIVITY, label: i18n._('Activity') },
                { value: PARTNER_TYPE_REFERRAL, label: i18n._('Referral') },
            ],
        },
        companyName: { required: true, type: 'text', label: i18n._('Company name') },
        phone: { required: true, type: 'text', label: i18n._('Phone') },
        address: { required: true, type: 'text', label: i18n._('Address') },
        city: { required: true, type: 'text', label: i18n._('City') },
        zipCode: { required: true, type: 'text', label: i18n._('Zip code') },
        companyCountry: { required: true, type: 'text', label: i18n._('My company is located in') },
        vatNumber: { required: true, type: 'text', label: i18n._('VAT number') },
        insurranceCompany: { required: true, type: 'text', label: i18n._('Insurance company') },
    }) as const satisfies {
        [key: string]: {
            required: boolean;
            type: getInputComponent.FieldType;
            label: string;
            options?: { [key: string]: string }[];
            placeholder?: string;
            id?: string;
        };
    };
